import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";
import BeneficiarySearchNSelect from "../../../Eligibility/components/requestEligibility/BeneficiarySearchNSelect";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import {
  withReferralInSupportingInfo,
  withoutAutoReferralInSupportingInfo,
} from "../../../../utils/referralSupportingInfo";



function getInitials(name) {
  if (!name) return "PA";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function calculateAge(dob) {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : 0;
}

function BeneficiaryInfo({
  title,
  onChange,
  refId,
  setSelectedPatient = () => { },
  selectedPatient = null,
  showPatientDocumentId = false,
  headerActions = null,
}) {
  const { t, i18n } = useTranslation();
  const {
    values,
    errors,
    touched,
    setFieldValue,
  } = useFormikContext();

  const navigate = useNavigate();
  const [insurancePlans, setInsurancePlans] = useState([]);
  const [localPatient, setLocalPatient] = useState(null);

  const formatInsurancePlansData = (data) => {
    let returnData = [];
    if (data?.length > 0) {
      data.map((item) => {
        returnData.push({
          id: item.id,
          member_card_id: item.member_card_id,
          policy_number: item.policy_number,
          expiry_date: item.expiry_date,
          isPrimary: item.isPrimary,
          payerId: item?.payer?.id,
          tpaId: item?.payer?.tpa_id,
          relation_with_subscriber: item.relation_with_subscriber,
          coverage_type: item.coverage_type,
          patient_share: item.patient_share,
          maxLimit: item.maxLimit,
          payerNphiesId: item.payerNphiesId,
          patientId: item.patientId,
          name: item?.payer?.name_en,
          license_id: item?.payer?.license_id,
        });
      });
    }
    return returnData;
  };

  const formikRef = React.useRef();
  React.useImperativeHandle(refId, () => ({
    Submit: async () => {
      await formikRef.current.submitForm();
    },
  }));

  const setInsuranceValue = (data, setFieldValue) => {
    for (let i = 0; i < data.length; i++) {
      const element = data[i];

      if (element.isPrimary) {
        setFieldValue("insurancePlanId", element.id);
        setFieldValue("payerId", element.payerId);
        setFieldValue("payerName", element.name);
        setFieldValue("payerLicenseId", element.license_id || "");
        setFieldValue("payerTpaId", element.tpaId || "");
        setFieldValue("memberCardId", element.member_card_id || "");
        setFieldValue("insurancePlanIndex", i);
      }
    }
  };

  useEffect(() => {
    if (insurancePlans && insurancePlans.length > 0) {
      setFieldValue("insurancePlanIndex", 0);
      setFieldValue("insurancePlanId", insurancePlans[0].id);
      setFieldValue("payerId", insurancePlans[0]?.payerId);
      setFieldValue("payerName", insurancePlans[0].name);
      setFieldValue("payerLicenseId", insurancePlans[0]?.license_id || "");
      setFieldValue("payerTpaId", insurancePlans[0]?.tpaId || "");
      setFieldValue("memberCardId", insurancePlans[0]?.member_card_id || "");
    } else {
      setFieldValue("insurancePlanId", "");
      setFieldValue("payerName", "");
      setFieldValue("payerId", "");
      setFieldValue("payerLicenseId", "");
      setFieldValue("payerTpaId", "");
      setFieldValue("memberCardId", "");
    }
  }, [insurancePlans]);

  const SmallToggle = ({ label, checked, onChange, id }) => {
    const isRtl = i18n.language === "ar";

    return (
      <div
        className="flex items-center gap-x-2 cursor-pointer group shrink-0"
        onClick={onChange}
      >
        <div
          className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 ease-in-out ${checked ? "bg-[#0070C2]" : "bg-gray-300"
            }`}
        >
          <span
            className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${isRtl
                ? checked
                  ? "-translate-x-[18px]"
                  : "-translate-x-[2px]"
                : checked
                  ? "translate-x-[18px]"
                  : "translate-x-[2px]"
              }`}
          />
        </div>
        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tight select-none">
          {label}
        </span>
      </div>
    );
  };

  const showNewBorn = values.isNewBorn;
  const showInsurance = values.patientId && !values.isFollowUpReq;

  const handleReferralInChange = () => {
    const nextValue = !values.isReferralIn;
    setFieldValue("isReferralIn", nextValue);
    setFieldValue(
      "supportingInfos",
      nextValue
        ? withReferralInSupportingInfo(values.supportingInfos)
        : withoutAutoReferralInSupportingInfo(values.supportingInfos),
    );
  };

  const selectedPlan =
    values.insurancePlanIndex !== "" && insurancePlans[values.insurancePlanIndex]
      ? insurancePlans[values.insurancePlanIndex]
      : insurancePlans.length > 0
        ? insurancePlans[0]
        : null;

  const patientDob = localPatient?.dob || selectedPatient?.dob || values?.patientDob || values?.dob;
  const patientAge = patientDob !== undefined && patientDob !== null ? calculateAge(patientDob) : (localPatient?.age ?? selectedPatient?.age ?? values?.patientAge ?? values?.age ?? null);
  const patientGender = localPatient?.gender || selectedPatient?.gender || values?.gender;

  const itemColSpan = showNewBorn ? "lg:col-span-3" : "lg:col-span-4";


  const currentBeneficiary = values.patientId
    ? {
      id: values.patientId,
      name: values.patientName,
      documentId: values.patientDocumentId || localPatient?.documentId || "",
      dob: values.patientDob || localPatient?.dob || selectedPatient?.dob,
      gender:
        values.patientGender || localPatient?.gender || selectedPatient?.gender,
    }
    : null;
  return (
    <div className="w-full">
      {/* ROW 1: Heading, Back Arrow & Switch Toggles */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-gray-200 pb-2 mb-2.5">
        {/* Left: Back Arrow + Page Title */}
        <div className="flex items-center gap-2">
          <div
            className="back flex items-center justify-center cursor-pointer rounded-full p-1 !w-6 !h-6 !bg-[#0070C2] hover:!bg-[#002699] transition-colors shrink-0"
            onClick={() => navigate(-1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className={`${i18n.language === "ar" ? "rotate-180" : ""}`}
            >
              <path
                d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                fill="white"
              />
            </svg>
          </div>
          <h2 className="text-sm font-semibold leading-6 text-gray-900">
            {title || t("PreAuth Req. Form")}
          </h2>
        </div>

        {/* Right: Switch Toggles */}
        <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-2">
          <SmallToggle
            id="newborn"
            label={t("New Born?")}
            checked={values.isNewBorn}
            onChange={() => setFieldValue("isNewBorn", !values.isNewBorn)}
          />
          <SmallToggle
            id="referral"
            label={t("Referral Out")}
            checked={values.isReferral}
            onChange={() => setFieldValue("isReferral", !values.isReferral)}
          />
          <SmallToggle
            id="referral-in"
            label={t("Referral In")}
            checked={values.isReferralIn}
            onChange={handleReferralInChange}
          />
          {headerActions}
        </div>
      </div>

      {/* ROW 2: Form Inputs Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2 w-full items-start">
        {/* 1. Patient Search Input */}
        <div className={`w-full ${itemColSpan}`}>
          <div className="flex flex-nowrap items-center gap-1 mb-1 overflow-hidden">
            <label
              htmlFor="myInput"
              className="text-[10px] leading-tight uppercase font-semibold text-gray-700 shrink-0"
            >
              {t("Beneficiary")}
            </label>
            {values.patientName && (
              <div className="min-w-0 flex-1 overflow-hidden">
                <Tooltip title={values.patientName} arrow>
                  <span
                    className="text-[10px] text-gray-500 cursor-default block"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    ({values.patientName})
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
          <BeneficiarySearchNSelect
            value={currentBeneficiary}
            setFieldValue={(val) => {
              setLocalPatient(val);
              if (values.isNewBorn) {
                setFieldValue("insurancePlanIndex", "");
              } else {
                let formattedIPlans = formatInsurancePlansData(
                  val.insurancePlans,
                );
                setInsurancePlans(formattedIPlans);
                setInsuranceValue(formattedIPlans, setFieldValue);
              }
              setFieldValue("patientId", val.id);
              setFieldValue("patientName", val.name);
              setFieldValue("patientDocumentId", val.documentId || "");
              if (val.dob) setFieldValue("patientDob", val.dob);
              if (val.gender) setFieldValue("patientGender", val.gender);
              setSelectedPatient(val);
            }}
          />
          {errors.patientId && touched.patientId ? (
            <p className="max-w-2xl text-xs text-red-500 mt-0.5">
              {errors.patientId}
            </p>
          ) : null}
        </div>

        {/* Subscriber for New Born */}
        {showNewBorn && (
          <div className={`w-full ${itemColSpan}`}>
            <div className="flex flex-nowrap items-center gap-1 mb-1 overflow-hidden">
              <label
                htmlFor="subscriberInput"
                className="text-[10px] leading-tight uppercase font-semibold text-gray-700 capitalize shrink-0"
              >
                {t("Subscriber")}
              </label>
              {values.subscriberName && (
                <div className="min-w-0 flex-1 overflow-hidden">
                  <Tooltip title={values.subscriberName} arrow>
                    <span
                      className="text-[10px] text-gray-500 cursor-default block"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      ({values.subscriberName})
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
            <BeneficiarySearchNSelect
              setFieldValue={(val) => {
                if (values.isNewBorn) {
                  let formattedIPlans = formatInsurancePlansData(
                    val.insurancePlans,
                  );
                  setInsurancePlans(formattedIPlans);
                  setInsuranceValue(formattedIPlans, setFieldValue);
                }
                setFieldValue("subscriber", val.id);
                setFieldValue("subscriberName", val.name);
                setSelectedPatient(val);
              }}
            />
          </div>
        )}

        {/* 2. Insurance Plan Input */}
        {showInsurance && (
          <div className={`w-full ${itemColSpan}`}>
            <div className="flex flex-nowrap items-center gap-1 mb-1 overflow-hidden">
              <label
                htmlFor="insurancePlanIndex"
                className="text-[10px] leading-tight uppercase font-semibold text-gray-700 shrink-0"
              >
                {t("Insurance Plans")}
                <span className="text-sm text-red-500"> *</span>
              </label>
              {values.payerName && (
                <div className="min-w-0 flex-1 overflow-hidden">
                  <Tooltip title={values.payerName} arrow>
                    <span
                      className="text-[10px] text-gray-500 cursor-default block"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      ({values.payerName})
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
            <select
              id="insurancePlanIndex"
              name="insurancePlanIndex"
              onChange={(e) => {
                let iPlanIndex = e.target.value;
                setFieldValue("insurancePlanIndex", iPlanIndex);

                if (iPlanIndex !== "") {
                  setFieldValue(
                    "insurancePlanId",
                    insurancePlans[iPlanIndex].id,
                  );
                  setFieldValue(
                    "payerId",
                    insurancePlans[iPlanIndex]?.payerId,
                  );
                  setFieldValue(
                    "payerName",
                    insurancePlans[iPlanIndex].name,
                  );
                  setFieldValue(
                    "payerLicenseId",
                    insurancePlans[iPlanIndex]?.license_id || "",
                  );
                  setFieldValue(
                    "payerTpaId",
                    insurancePlans[iPlanIndex]?.tpaId || "",
                  );
                  setFieldValue(
                    "memberCardId",
                    insurancePlans[iPlanIndex]?.member_card_id || "",
                  );
                } else {
                  setFieldValue("insurancePlanId", "");
                  setFieldValue("payerId", "");
                  setFieldValue("payerName", "");
                  setFieldValue("payerLicenseId", "");
                  setFieldValue("payerTpaId", "");
                  setFieldValue("memberCardId", "");
                }
              }}
              value={values.insurancePlanIndex}
              autoComplete="type-name"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
            >
              <option value="">{t("Please Select")}</option>
              {insurancePlans.map((item, index) => {
                return (
                  <option key={index} value={index}>
                    {item?.name} ({item?.member_card_id})
                  </option>
                );
              })}
            </select>
            {errors.insurancePlanId && touched.insurancePlanId ? (
              <p className="max-w-2xl text-xs text-red-500 mt-0.5">
                {errors.insurancePlanId}
              </p>
            ) : null}
          </div>
        )}
      </div>

      {/* Patient Info Strip (below inputs, one line) */}
      {values.patientId && (
        <div className="mt-2 w-full">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 bg-white rounded-lg border border-gray-200 px-3.5 py-2.5">
            <span className="inline-flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-[#0070C2] text-white flex items-center justify-center font-bold text-xs shrink-0">
                {getInitials(values.patientName)}
              </span>
              <strong className="text-[14px] font-bold text-gray-900 uppercase">
                {values.patientName}
              </strong>
            </span>

            <span className="text-[13px] text-gray-500 whitespace-nowrap">
              <strong className="font-bold text-gray-700">{t("Document ID")}:</strong>{" "}
              <strong className="font-semibold text-gray-800">{values.patientDocumentId || localPatient?.documentId || "N/A"}</strong>
            </span>
            <span className="text-[13px] text-gray-500 whitespace-nowrap">
              <strong className="font-bold text-gray-700">{t("Membership No")}:</strong>{" "}
              <strong className="font-semibold text-gray-800">{values.memberCardId || selectedPlan?.member_card_id || "N/A"}</strong>
            </span>
            {patientAge !== null && patientAge !== undefined ? (
              <span className="text-[13px] font-semibold text-gray-800 whitespace-nowrap">
                {patientAge} {t("Years")}
              </span>
            ) : null}
            {patientGender ? (
              <span className="text-[13px] font-semibold text-gray-800 capitalize whitespace-nowrap">
                {patientGender}
              </span>
            ) : null}
            <span className="text-[13px] text-gray-700 whitespace-nowrap">
              <strong className="font-bold text-gray-800">{t("Insurance Company")}:</strong>{" "}
              <strong className="font-bold text-[#0070C2]">
                {values.payerName || selectedPlan?.name || t("N/A")}
              </strong>
            </span>
            {values.payerTpaId || selectedPlan?.tpaId ? (
              <span className="inline-flex items-center rounded-full bg-blue-50 text-[#0070C2] border border-blue-100 px-2.5 py-0.5 text-[11px] font-bold uppercase">
                TPA
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default BeneficiaryInfo;
